import React, {ChangeEvent, useState} from 'react';
import b_ from 'b_';

import {Select, Textarea} from 'client/common/components/lego';
import {
    useIsAuthorized,
    useIsSuperuser,
    useIsSupport,
    useIsYandexPublisher
} from 'client/common/hooks/user';
import {useAudienceTargeting} from 'client/common/hooks/audience-targeting';
import {TargetingsProps} from 'client/common/components/audience-settings/types';

import {SurveyScope} from 'client/common/types';
import {useSurvey, useUpdateSurveySettings} from 'client/common/hooks/survey';
import {useSurveySlug} from 'client/common/contexts/slug';
import ScopeSelect from 'client/common/components/survey-settings/modals/edit-settings/scope-select';
import SurveyLanguage from 'client/common/components/survey-settings/survey-language';
import SurveyTextDirectionSelect from 'client/common/components/survey-settings/survey-text-direction-select';
import BlockInput from 'client/common/components/block-input';
import SurveyRedirect from 'client/common/components/survey-settings/survey-redirect';
import {convertToLegoOptions} from 'client/common/components/audience-settings/helpers';
import {useCanEditAudience} from 'client/common/hooks/edit-mode';
import cfg from 'client/common/utils/client-config';
import {getIsCreativeBox} from 'client/common/utils/box';

import I18N from 'assets/i18n/brandlift-project';

import './audience-manual.css';

const i18n = I18N.surveyEditor.audienceTab.manual;
const i18nAudienceTargeting = I18N.common.audienceSettings.audienceTargeting;

const b = b_.with('audience-manual');

// eslint-disable-next-line max-statements, complexity
export default function AudienceManual(
    props: Pick<TargetingsProps, 'boxType' | 'request' | 'categories' | 'interests' | 'onChange'>
) {
    const {boxType, request, categories, interests, onChange} = props;

    const slug = useSurveySlug();
    const survey = useSurvey(slug);
    const isAuthorized = useIsAuthorized();
    const canEdit = useCanEditAudience();
    const [scope, setScope] = useState(survey.data?.settings.scope[0]);

    const onUpdate = useUpdateSurveySettings(slug);

    const isYandexPublisher = useIsYandexPublisher();
    const isSupport = useIsSupport();
    const isSuperuser = useIsSuperuser();

    const {
        handleDescriptionChange,
        handleCountryChangeEvent,
        countriesOptions,
        selectedCountry,
        description
    } = useAudienceTargeting({
        boxType,
        request,
        categories,
        interests,
        onChange
    });

    const handleScopeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
        const newScope = event.target.value as SurveyScope;

        setScope(newScope);
        await onUpdate({
            ...(newScope !== undefined && {scope: [newScope]})
        });
    };

    const insider = isYandexPublisher || isSuperuser || isSupport;

    const shouldDisplayFull = !getIsCreativeBox(boxType) && boxType !== 'ux' && insider;

    return (
        <div className={b('')}>
            {insider && (
                <>
                    <h2 className={b('title')}>{i18n.title}</h2>

                    <p className={b('description')}>
                        {i18n.description}
                        <a href={cfg.supplierLink} target='_blank' rel='noopener noreferrer'>
                            {cfg.supplierLink}
                        </a>
                    </p>
                </>
            )}

            {shouldDisplayFull && (
                <>
                    <BlockInput title={i18nAudienceTargeting.countryTitle}>
                        <Select
                            className={`${b('country-select')}`}
                            onChange={handleCountryChangeEvent}
                            options={convertToLegoOptions(countriesOptions)}
                            value={selectedCountry}
                            disabled={!canEdit}
                            size='m'
                            width='max'
                            view='dl-clear'
                        />
                    </BlockInput>

                    <SurveyLanguage />
                    {isSuperuser && <SurveyTextDirectionSelect />}

                    {isAuthorized && (
                        <ScopeSelect scope={scope} handleScopeChange={handleScopeChange} />
                    )}

                    <SurveyRedirect />
                </>
            )}
            <BlockInput>
                <Textarea
                    placeholder={i18nAudienceTargeting.placeholder.manualDescription}
                    size='m'
                    view='default'
                    defaultValue={description}
                    disabled={!(isSuperuser || isSupport || isYandexPublisher)}
                    onBlur={handleDescriptionChange}
                    rows={4}
                />
            </BlockInput>
        </div>
    );
}
